import React, { useState, useRef } from 'react';
import { downloadPdfReport, validateBatch, validateCSV } from '../api/client';
import StatusBadge from './StatusBadge';

const EXAMPLE_CSV = `id;freitext;icd_code
REC-001;Schmerzen im linken Fuß;M25.37
REC-002;Knieschmerzen rechts seit 3 Wochen;M25.36
REC-003;Kreuzschmerzen nach langem Sitzen;M54.5
REC-004;Bluthochdruck kontrolliert;I10
REC-005;Magenschmerzen nach dem Essen;M54.5
REC-006;Erkältung mit Husten und Schnupfen;J06.9
REC-007;Diabetes Typ 2 Kontrolle Blutzucker;E11.9
REC-008;Neurodermitis Juckreiz Hautausschlag;L20.9`;

const FILTERS = [
  { key: 'all',      label: 'Alle' },
  { key: 'ok',       label: 'Nur OK' },
  { key: 'warning',  label: 'Nur Warnungen' },
  { key: 'error',    label: 'Nur Fehler' },
  { key: 'uncertain', label: 'Nur Unklar' },
  { key: 'review',   label: 'Manuell prüfen' },
];

function exportToCsv(results) {
  const headers = [
    'id', 'freitext', 'icd_code', 'status', 'confidence',
    'message', 'reasons', 'suggestions', 'manual_review_required',
  ];
  const rows = results.map(r => [
    r.id || '',
    r.freitext || '',
    r.icd_code || '',
    r.status || '',
    r.confidence ?? '',
    r.message || '',
    (r.reasons || []).join(' | '),
    (r.suggestions || []).map(s => `${s.code}: ${s.desc}`).join(' | '),
    r.manual_review_required ? 'ja' : 'nein',
  ]);

  const escape = (val) => `"${String(val).replace(/"/g, '""')}"`;
  const csvContent = [headers, ...rows]
    .map(row => row.map(escape).join(';'))
    .join('\n');

  const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = `icd10-validierung-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function BatchValidator({ onResult }) {
  const [mode, setMode]       = useState('manual');
  const [rows, setRows]       = useState([{ id: '', freitext: '', icd_code: '' }]);
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('batch-pruefung.csv');
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [filter, setFilter]   = useState('all');
  const fileRef               = useRef();

  const addRow    = () => setRows(r => [...r, { id: '', freitext: '', icd_code: '' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) =>
    setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const loadExample = () => { setCsvText(EXAMPLE_CSV); setFileName('beispieldaten.csv'); setMode('csv'); };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name || 'csv-upload.csv');
    const reader = new FileReader();
    reader.onload = (ev) => { setCsvText(ev.target.result); setMode('csv'); };
    reader.readAsText(file, 'utf-8');
  };

  const run = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      let data;
      if (mode === 'csv') {
        data = await validateCSV(csvText, fileName || 'csv-upload.csv');
      } else {
        const valid = rows.filter(r => r.freitext.trim() && r.icd_code.trim());
        if (!valid.length) {
          setError('Mindestens eine vollständige Zeile erforderlich.');
          setLoading(false);
          return;
        }
        data = await validateBatch(valid, fileName || 'manuelle-batch-pruefung');
      }
      setResult(data);
      onResult?.(data);
      setFilter('all');
    } catch (e) {
      setError(e.response?.data?.detail || 'API nicht erreichbar — läuft der Server auf Port 8000?');
    } finally {
      setLoading(false);
    }
  };

  const filterCount = (key) => {
    if (!result?.results) return 0;
    const all = result.results;
    switch (key) {
      case 'ok':        return all.filter(r => r.status === 'ok').length;
      case 'warning':   return all.filter(r => r.status === 'warning').length;
      case 'error':     return all.filter(r => r.status === 'error').length;
      case 'uncertain': return all.filter(r => r.status === 'uncertain').length;
      case 'review':    return all.filter(r => r.manual_review_required).length;
      default:          return all.length;
    }
  };

  const filtered = (result?.results || []).filter(r => {
    switch (filter) {
      case 'ok':        return r.status === 'ok';
      case 'warning':   return r.status === 'warning';
      case 'error':     return r.status === 'error';
      case 'uncertain': return r.status === 'uncertain';
      case 'review':    return r.manual_review_required;
      default:          return true;
    }
  });

  return (
    <div style={{ animation: 'fadeUp 0.4s ease' }}>
      {/* Disclaimer */}
      <div style={{
        marginBottom: 20, padding: '10px 14px',
        background: 'rgba(74,240,196,0.05)', border: '1px solid rgba(74,240,196,0.2)',
        borderRadius: 8, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6,
      }}>
        Dieses Tool unterstützt nur die <strong style={{ color: 'var(--text)' }}>Plausibilitätsprüfung</strong> von ICD-10-Codes.
        Es ersetzt keine medizinische oder abrechnungsrechtliche Prüfung. Manuelle Kontrolle bleibt erforderlich.
      </div>

      {/* Mode Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['manual', 'Manuell eingeben'], ['csv', 'CSV / Datei']].map(([m, label]) => (
          <button key={m} onClick={() => setMode(m)} style={{
            padding: '8px 18px', fontSize: 13, fontWeight: 600,
            background: mode === m ? 'var(--accent)' : 'var(--surface)',
            color: mode === m ? '#0a1a14' : 'var(--text2)',
            border: `1px solid ${mode === m ? 'var(--accent)' : 'var(--border)'}`,
          }}>{label}</button>
        ))}
      </div>

      {mode === 'manual' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 140px 36px', gap: 8, marginBottom: 8 }}>
            {['ID (opt.)', 'Freitext-Diagnose', 'ICD-10 Code', ''].map((h, i) => (
              <span key={i} style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'Arial, sans-serif', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</span>
            ))}
          </div>
          {rows.map((row, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 140px 36px', gap: 8, marginBottom: 8 }}>
              <input value={row.id} onChange={e => updateRow(i, 'id', e.target.value)} placeholder={`REC-${String(i+1).padStart(3,'0')}`} style={{ fontSize: 12 }} />
              <input value={row.freitext} onChange={e => updateRow(i, 'freitext', e.target.value)} placeholder="Diagnose Freitext..." />
              <input value={row.icd_code} onChange={e => updateRow(i, 'icd_code', e.target.value)} placeholder="M25.5" style={{ fontFamily: 'Consolas, monospace', letterSpacing: '0.05em' }} />
              <button onClick={() => removeRow(i)} disabled={rows.length === 1} style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text3)', fontSize: 18, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
          ))}
          <button onClick={addRow} style={{ marginTop: 4, padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13 }}>+ Zeile hinzufügen</button>
        </div>
      )}

      {mode === 'csv' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <button onClick={() => fileRef.current.click()} style={{ padding: '9px 18px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 13 }}>
              CSV-Datei öffnen
            </button>
            <button onClick={loadExample} style={{ padding: '9px 18px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13 }}>
              Beispieldaten laden
            </button>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} style={{ display: 'none' }} />
          </div>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'Arial, sans-serif', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              CSV-Inhalt (Semikolon-getrennt: id;freitext;icd_code)
            </span>
          </div>
          <input
            value={fileName}
            onChange={e => setFileName(e.target.value)}
            placeholder="Dateiname fuer History"
            style={{ marginBottom: 10 }}
          />
          <textarea
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
            rows={8}
            placeholder={`id;freitext;icd_code\nREC-001;Knieschmerz links;M25.36\n...`}
            style={{ fontFamily: 'Consolas, monospace', fontSize: 12, letterSpacing: '0.02em', lineHeight: 1.7, resize: 'vertical' }}
          />
        </div>
      )}

      <button onClick={run} disabled={loading} style={{
        marginTop: 16, padding: '12px 28px',
        background: loading ? 'var(--surface2)' : 'var(--accent)',
        color: loading ? 'var(--text3)' : '#0a1a14',
        fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
        cursor: loading ? 'not-allowed' : 'pointer', width: '100%',
      }}>
        {loading ? 'Validiere...' : 'Batch validieren →'}
      </button>

      {error && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--error-bg)', border: '1px solid var(--error-border)', borderRadius: 8, color: 'var(--error)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {result && (
        <BatchResults
          result={result}
          filter={filter}
          setFilter={setFilter}
          filtered={filtered}
          filterCount={filterCount}
          onExport={() => exportToCsv(result.results)}
          onPdf={result.job ? () => downloadPdfReport(result.job.job_id) : null}
        />
      )}
    </div>
  );
}

function BatchResults({ result, filter, setFilter, filtered, filterCount, onExport, onPdf }) {
  const { summary } = result;
  const pct = summary.auto_approved_rate;

  return (
    <div style={{ marginTop: 24, animation: 'fadeUp 0.3s ease' }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 16 }}>
        <SummaryCard label="Gesamt"    value={summary.total}                 color="var(--text)" />
        <SummaryCard label="OK"        value={summary.ok}                    color="var(--ok)" />
        <SummaryCard label="Warnungen" value={summary.warning}               color="var(--warn)" />
        <SummaryCard label="Fehler"    value={summary.error}                  color="var(--error)" />
        <SummaryCard label="Auto-Rate" value={`${pct}%`}                     color="var(--accent)" />
      </div>

      {/* Progress bar */}
      <div style={{ height: 5, background: 'var(--surface2)', borderRadius: 4, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--ok)', borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>

      {/* Filter + Export */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: '6px 12px', fontSize: 11, fontWeight: 600,
                background: filter === key ? 'var(--surface2)' : 'transparent',
                border: `1px solid ${filter === key ? 'var(--border2)' : 'transparent'}`,
                color: filter === key ? 'var(--text)' : 'var(--text3)',
              }}
            >
              {label} ({filterCount(key)})
            </button>
          ))}
        </div>
        <button
          onClick={onExport}
          style={{
            padding: '7px 16px', fontSize: 12, fontWeight: 600,
            background: 'var(--surface)', border: '1px solid var(--border)',
            color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          Ergebnis als CSV herunterladen
        </button>
        {onPdf && (
          <button
            onClick={onPdf}
            style={{
              padding: '7px 16px', fontSize: 12, fontWeight: 600,
              background: 'var(--accent)', border: '1px solid var(--accent)',
              color: '#ffffff', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            PDF-Report herunterladen
          </button>
        )}
      </div>

      {/* Results table */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 110px 130px 110px', gap: 0, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface2)' }}>
          {['ID', 'Freitext', 'Code', 'Bedeutet', 'Status'].map((h, i) => (
            <span key={i} style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{h}</span>
          ))}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Keine Einträge</div>
        )}
        {filtered.map((r, i) => (
          <ResultRow key={i} r={r} last={i === filtered.length - 1} />
        ))}
      </div>
    </div>
  );
}

function ResultRow({ r, last }) {
  const [open, setOpen] = useState(false);
  const hasDetail = (r.suggestions?.length > 0) || (r.reasons?.length > 0);
  const rowBg = r.status === 'error' ? 'rgba(255,91,91,0.03)' :
                r.status === 'warning' ? 'rgba(245,166,35,0.03)' : 'transparent';

  return (
    <>
      <div
        onClick={() => hasDetail && setOpen(o => !o)}
        style={{
          display: 'grid', gridTemplateColumns: '80px 1fr 110px 130px 110px',
          gap: 0, padding: '12px 16px', background: rowBg,
          borderBottom: last && !open ? 'none' : '1px solid var(--border)',
          cursor: hasDetail ? 'pointer' : 'default',
          transition: 'background 0.1s',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'Consolas, monospace', alignSelf: 'center' }}>{r.id || '—'}</span>
        <span style={{ fontSize: 13, color: 'var(--text)', alignSelf: 'center', paddingRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.freitext}>{r.freitext}</span>
        <span style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'Consolas, monospace', letterSpacing: '0.05em', alignSelf: 'center' }}>{r.icd_code}</span>
        <span style={{ fontSize: 12, color: r.manual_review_required ? 'var(--warn)' : 'var(--text2)', alignSelf: 'center', paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.icd_desc}>{r.icd_desc}</span>
        <span style={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6 }}>
          <StatusBadge status={r.status} size="sm" />
          {hasDetail && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{open ? '▲' : '▼'}</span>}
        </span>
      </div>

      {open && hasDetail && (
        <div style={{ padding: '10px 16px 14px 96px', background: 'var(--surface2)', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
          {r.reasons?.length > 0 && (
            <>
              <p style={detailLabel}>Begründungen</p>
              {r.reasons.map((reason, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                  <span style={{ color: 'var(--text3)', fontSize: 11, flexShrink: 0 }}>→</span>
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>{reason}</span>
                </div>
              ))}
            </>
          )}
          {r.suggestions?.length > 0 && (
            <>
              <p style={{ ...detailLabel, marginTop: r.reasons?.length > 0 ? 10 : 0 }}>Vorschläge</p>
              {r.suggestions.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'Consolas, monospace', fontSize: 12, color: 'var(--accent)', minWidth: 65 }}>{s.code}</span>
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>{s.desc}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 }}>
      <p style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'Arial, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>{label}</p>
      <p style={{ fontSize: 22, fontFamily: 'Arial, sans-serif', fontWeight: 700, color }}>{value}</p>
    </div>
  );
}

const detailLabel = {
  fontSize: 10, color: 'var(--text3)', fontFamily: 'Arial, sans-serif',
  fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6,
};
